import {
  Divider,
  Icon,
  IconColors,
  Logo,
  LogoNames,
  Text,
  useToast,
  useIsBreakpointLargeAndUp,
  Avatar,
  useTheme,
  ToastItem,
  MultiClickZone,
  useIsDesignLanguage,
  DesignLanguage,
} from '@afterpaytouch/core'
import clsx from 'clsx'
import { useTranslation } from 'next-i18next'
import React, { FunctionComponent, useEffect, useRef } from 'react'
import { Country } from '@afterpaytouch/portal-api/types'
import {
  SessionIdVisibility,
  CheckoutStatus,
  isCountryNeedsBiometricVerification,
  CLICKS_TO_REVEAL_SESSION_ID,
  RegistrationStatus,
  TrackingEvent,
} from '../../model'
import { CloseCheckout } from '../CloseCheckout'
import {
  resetRegistrationState,
  setSessionIdVisibility,
  useAllowedQuery,
  useSessionId,
  useAppDispatch,
  useAppropriatePaymentMethod,
  useCashAppConsumerName,
  useCheckoutState,
  useConsumerLendingEligible,
  useConsumerName,
  useIsAfterpayAppGiftCardFlow,
  useIsAfterpayFinanceFeeEligible,
  useRegistration,
  useSummaryLayout,
} from '../../state'
import styles from './style.module.css'
import { useRouter } from 'next/router'
import { useRoutePageName, Route } from '../../config/router'
import { HeaderType } from '../../layouts/types'
import { BackLink } from '../BackLink'
import { HeaderProps } from './types'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { AFFHeader } from './AFFHeader'
import { useHideDividerForInlineArcadeComponents } from './hooks'

const getLogoName = (country: Country): LogoNames => {
  switch (country) {
    case Country.GB:
      return 'Clearpay'
    default:
      return 'Afterpay'
  }
}

export const HeaderCloseButton: FunctionComponent<{ heroCloseButtonColour?: IconColors; noMarginLeft?: boolean }> = ({
  heroCloseButtonColour,
  noMarginLeft = false,
}) => {
  const { t } = useTranslation()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const pageName = useRoutePageName()
  const isDesktop = useIsBreakpointLargeAndUp() ?? false
  const { theme } = useTheme()
  const heroCloseButtonStyle = clsx(
    isDesktop && heroCloseButtonColour && styles.heroCloseButtonColourDesktop,
    heroCloseButtonColour ? styles.heroCloseButtonColour : styles.closeWrapper,
    theme.designLanguage === DesignLanguage.Arcade && styles.cashCloseWrapper,
    noMarginLeft && styles.noMarginLeft
  )

  return (
    <div className={heroCloseButtonStyle}>
      <CloseCheckout
        aria-label={t('common:closeCheckoutAria')}
        size='Regular'
        kind='Link'
        testNameSpace='close-checkout'
        onClick={() => logEvent(`Pressed Close on ${pageName} Page`)}
      >
        <Icon.Close size='Small' color={heroCloseButtonColour ?? 'Black'} />
      </CloseCheckout>
    </div>
  )
}

export const Header: FunctionComponent<HeaderProps> = ({
  allowClose = true,
  allowHeader = true,
  backLink,
  country,
  type = HeaderType.Standard,
  testNameSpace = '',
  heroCloseButtonColour,
}) => {
  const { t } = useTranslation()
  const logo = getLogoName(country)
  const setToast = useToast()
  const isArcadeDesign = useIsDesignLanguage(DesignLanguage.Arcade)
  const { status: registrationStatus } = useRegistration()
  const { status: checkoutStatus } = useCheckoutState()
  const dispatch = useAppDispatch()
  const consumerLendingEligible = useConsumerLendingEligible()
  const isAfterpayAppGiftCardFlow = useIsAfterpayAppGiftCardFlow()
  const hideDividerForArcadeInlingComponentExperiment = useHideDividerForInlineArcadeComponents()
  const showDivider = !hideDividerForArcadeInlingComponentExperiment && !isAfterpayAppGiftCardFlow
  const profileConsumerName = useConsumerName()
  const cashAppConsumerName = useCashAppConsumerName()
  const { variant: summaryVariant } = useSummaryLayout()
  const router = useRouter()
  const avatarInHeader = summaryVariant === 'E' && (router.pathname === Route.SUMMARY || router.pathname === Route.PAYMENT_METHOD)
  const isAFF = useIsAfterpayFinanceFeeEligible()
  const consumerName = profileConsumerName.length > 0 ? profileConsumerName : cashAppConsumerName
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const logoSize = isArcadeDesign ? 'Medium' : 'Small'
  const paymentMethod = useAppropriatePaymentMethod()
  const hasNoPaymentMethod = paymentMethod === null
  const { visibility: sessionIdVisibility } = useSessionId()
  const { hideHeader } = useAllowedQuery()
  const shouldRenderCreateAccountBody = country !== Country.AU && hasNoPaymentMethod
  const shouldShowOneTimeOnlyToast = useRef(true)

  const accountCreated = (): JSX.Element =>
    shouldRenderCreateAccountBody ? (
      <>
        <ToastItem.Headline testNameSpace='account-created-heading'>{t('common:accountCreatedHeader')}</ToastItem.Headline>
        <ToastItem.Body testNameSpace='account-created-body'>{t('common:accountCreatedMessage')}</ToastItem.Body>
      </>
    ) : (
      <>
        <ToastItem.Body testNameSpace='account-created-heading'>{t('common:accountCreatedHeader')}</ToastItem.Body>
      </>
    )

  const accountVerified = (): JSX.Element =>
    hasNoPaymentMethod ? (
      <>
        <ToastItem.Headline testNameSpace='account-verified-heading'>{t('common:accountVerifiedHeader')}</ToastItem.Headline>
        <ToastItem.Body testNameSpace='account-created-body'>{t('common:accountVerifiedMessage')}</ToastItem.Body>
      </>
    ) : (
      <>
        <ToastItem.Body testNameSpace='account-verified-heading'>{t('common:accountVerifiedHeader')}</ToastItem.Body>
      </>
    )

  const accountCreatedForConsumerLending = (): JSX.Element => (
    <>
      <Text size='L' bold>
        {t('common:accountCreatedHeaderCL')}
      </Text>
      <Text size='XS'>{t('common:accountCreatedMessageCL')}</Text>
    </>
  )

  useEffect(() => {
    // 1. Au has one more flow after registration completed, so we need to check the registration status
    // 2. If the order is consumer lending eligible, we need to check the consumer lending eligible status
    // 3. show the id verified toast for consumers who have need the biometric verification(means they passed the soft id attempt)
    const isRegistrationCompleted =
      country === Country.AU && !consumerLendingEligible
        ? registrationStatus === RegistrationStatus.ContractAgreementAccepted
        : registrationStatus === RegistrationStatus.Completed

    const needsBiometricVerification = isCountryNeedsBiometricVerification(country)
    const isBiometricVerified = needsBiometricVerification && isRegistrationCompleted
    const isIdVerified = registrationStatus === RegistrationStatus.IDVerified || isBiometricVerified

    // Toast should be shown only once
    const showToast = (isIdVerified || isRegistrationCompleted) && checkoutStatus === CheckoutStatus.Started && shouldShowOneTimeOnlyToast.current

    if (showToast) {
      if (consumerLendingEligible) {
        setToast({
          message: accountCreatedForConsumerLending,
          onShow: () => dispatch(resetRegistrationState()),
          kind: 'Success',
          toastTime: 10000,
          testNameSpace: 'account-created-header',
        })
      } else {
        // Prefer show the id verified toast in the verifying page for UK Idv signup
        setToast({
          message: isIdVerified ? accountVerified : accountCreated,
          onShow: () => {
            if (isIdVerified) {
              logEvent(TrackingEvent.VIEWED_ID_VERIFIED_TOAST, { registrationStatus })
            } else {
              logEvent(TrackingEvent.VIEWED_ACCOUNT_CREATED_TOAST, { registrationStatus })
            }
            dispatch(resetRegistrationState())
          },
          kind: 'Success',
          toastTime: 10000,
          testNameSpace: 'account-created-header',
        })
      }

      shouldShowOneTimeOnlyToast.current = false
    }
  }, [registrationStatus, checkoutStatus])

  const headerStyle = clsx(styles.header, type === HeaderType.Collapsed && styles.collapsed, isAfterpayAppGiftCardFlow && styles.marginNarrow)

  const onMultiClickTrigger = (): void => {
    const newVisibility = sessionIdVisibility === SessionIdVisibility.VISIBLE ? SessionIdVisibility.HIDDEN : SessionIdVisibility.VISIBLE
    dispatch(setSessionIdVisibility(newVisibility))
  }

  if (isAFF) {
    return <AFFHeader />
  }

  if (heroCloseButtonColour && !allowClose) {
    throw Error('Must have `allowClose` set to true, if setting `heroCloseButtonColour` prop')
  }

  if (heroCloseButtonColour) {
    return <HeaderCloseButton heroCloseButtonColour={heroCloseButtonColour} />
  }

  if (allowHeader && !hideHeader) {
    return (
      <header>
        <div className={headerStyle} {...(testNameSpace.length > 0 && { 'data-testid': `${testNameSpace}-header` })}>
          <div className={styles.headerBody}>
            <div className={styles.standardHeader}>
              <div className={styles.headerLogoRow}>
                <MultiClickZone onMultiClickTrigger={onMultiClickTrigger} testNameSpace='header' numOfClicks={CLICKS_TO_REVEAL_SESSION_ID} />
                {avatarInHeader && <Avatar size='S' name={consumerName} alt={consumerName} title={consumerName} testNameSpace='header-avatar' />}
                <Logo name={logo} country={country} title={t('common:brandName')} description={t('common:brandName')} size={logoSize} />
              </div>
            </div>
            <div className={styles.collapsedHeader}>{typeof backLink !== 'undefined' && <BackLink backTo={backLink} />}</div>
          </div>
          {allowClose && <HeaderCloseButton />}
        </div>
        {showDivider && <Divider kind='Hairline' testNameSpace='header' />}
      </header>
    )
  }

  return null
}
